import React, { useState } from 'react';
import { useAppContext } from '../../../AppContext';
import styled, { css } from 'styled-components';
import ProvidersModal from './ProvidersModal';
import CasesModal from './CasesModal';
// @ts-ignore
import styles from './PayAllTile.module.scss';
import withAbortRequest, { AbortRequestPropsType } from '../../../core/hoc/withAbortRequest';
import { PayAllTileModel } from './types/PayAllTileModel';
import { PayableItem } from './types/PayableItem';
import { Button, Loader, ModuleContainer } from '../../../core/components';
import ApiProvider from '../../../core/api/ApiProvider';
import Status from '../../../core/types/Status';
import { InfoMessageService, StyleService, TranslationService } from '../../../core/services';
import { PaymentProviderType } from '../../types/PaymentProviderType';
import { RedirectContentResponse } from '../../types/RedirectContentResponse';
import { DictionaryType } from '../../../core/types/DictionaryType';
import InstallmentPlanModal from './components/InstallmentPlanModal';
import classNames from 'classnames';
import { useQuery } from 'react-query';

const PayAllTile = (props: AbortRequestPropsType) => {
  const [isOpenProvidersModal, setIsOpenProvidersModal] = useState<boolean>(false);
  const [isOpenCasesModal, setIsOpenCasesModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<PayableItem>();
  const [isOpenInstallmentPlanModal, setIsOpenInstallmentPlanModal] = useState<boolean>(false);

  const context = useAppContext();
  const whitelabelingStyles = StyleService.applyMany(
    [
      'module:paynow:firstColor',
      'module:paynow:secondColor',
      'module:paynow:payButton',
      'module:paynow:applyButton',
    ],
    context.styles
  );

  const getPaymentUrl = async (selectedItem: PayableItem, providerType: PaymentProviderType) => {
    return await ApiProvider.post<Status<string>>(
      'PayAllTileModule/GetPaymentServiceUrl',
      {
        caseNo: selectedItem.caseGuid,
        providerType: providerType,
        sourceTenantId: selectedItem.sourceTenantId,
      },
      props.cancelTokenSource.token
    );
  };

  const payAllTile = useQuery<Status<PayAllTileModel>>(
    'payAllTile',
    () =>
      ApiProvider.get<Status<PayAllTileModel>>(
        'PayAllTileModule/GetPayAllTile',
        props.cancelTokenSource.token
      ),
    {
      refetchOnWindowFocus: false,
      onError: () => {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      },
    }
  );

  const goToPaymentsPage = async (selectedItem: PayableItem, provider: PaymentProviderType) => {
    var response = await getPaymentUrl(selectedItem, provider);
    if (response.success) {
      var redirectReponse = await ApiProvider.get<Status<RedirectContentResponse>>(response.data);
      if (redirectReponse.data.method === 'GET') {
        window.location.href = redirectReponse.data.url;
      } else {
        const r = document.createRange();
        const f = r.createContextualFragment(redirectReponse.data.body);
        document.getElementsByTagName('body')[0].appendChild(f);
        document.querySelector<HTMLButtonElement>('#submit-button')!.click();
      }
    } else {
      InfoMessageService.error(response.message);
    }
    setIsOpenCasesModal(false);
    setIsOpenProvidersModal(false);
  };

  const onPayAllTileClick = () => {
    if (payAllTile.isLoading) {
      return;
    }
    if (payAllTile.data?.data.items && payAllTile.data?.data.items.length > 1) {
      setIsOpenCasesModal(true);
    } else if (payAllTile.data?.data.items.length === 1) {
      if (payAllTile.data?.data.paymentProviderTypes.length > 1) {
        setSelectedItem(payAllTile.data?.data.items[0]);
        setIsOpenProvidersModal(true);
      } else {
        goToPaymentsPage(
          payAllTile.data?.data.items[0],
          payAllTile.data?.data.paymentProviderTypes[0]
        );
      }
    }
  };

  const payCase = (payCase: PayableItem) => {
    setSelectedItem(payCase);
    setIsOpenCasesModal(false);
    setIsOpenProvidersModal(true);
  };

  const onPay = (providerType: PaymentProviderType) => {
    if (!selectedItem) return;
    goToPaymentsPage(selectedItem, providerType);
  };

  const debtorHasSomeCases = (payAllTile.data?.data.numberOfCases ?? 0) > 0;

  const isPayNowButtonDisabled =
    payAllTile.data?.data.numberOfCases === 0 ||
    parseInt(payAllTile.data?.data.allCasesBalance ?? '0') <= 0 ||
    payAllTile.data?.data.allOpenCasesHasPendingPayment;

  return (
    <ModuleContainer variant={ModuleContainer.ModuleVariant.Secondary}>
      <PayAllTileDiv
        id="PayAllWrapper"
        className={`${styles.wrapper}`}
        styles={whitelabelingStyles}
      >
        {payAllTile.isLoading && <Loader opacity={0.4} />}

        {payAllTile.data?.data.displayBalanceAmount && (
          <div>
            <h2 style={{ color: 'white', paddingBottom: 5, fontWeight: 700 }}>
              {TranslationService.translate('SumOfAllCases')}
            </h2>
            <div id="SumAllCases" className={styles.amount}>
              {payAllTile.data?.data.allCasesBalance}
              <em className={styles.currencySymbol}>{payAllTile.data?.data.currency}</em>
            </div>
          </div>
        )}

        {debtorHasSomeCases && (
          <div className={styles.buttonsWrapper}>
            <Button
              id="PayNowButton"
              type={Button.Types.Button}
              variant={Button.Variants.Primary}
              classNames={classNames(styles.button, styles.pay)}
              disabled={isPayNowButtonDisabled}
              onClick={() => {
                if (isPayNowButtonDisabled) {
                  return undefined;
                }
                return payAllTile.data?.data.isPaymentsFuncTurnOn ? onPayAllTileClick() : undefined;
              }}
            >
              {payAllTile.data?.data.isPaymentsFuncTurnOn
                ? payAllTile.data?.data.allOpenCasesHasPendingPayment
                  ? TranslationService.translate('PaymentIsPending')
                  : TranslationService.translate('PayNow')
                : TranslationService.translate('TotalBalance')}
            </Button>

            {payAllTile.data?.data.showApplyForInstallmentButton && (
              <Button
                type={Button.Types.Button}
                size={Button.Sizes.LG}
                variant={Button.Variants.Danger}
                classNames={classNames(styles.button, styles.installmentsButton, 'mt-1')}
                disabled={isPayNowButtonDisabled}
                onClick={() => {
                  if (payAllTile.data?.data.items.length === 1) {
                    window.location.href = `CaseDetails/${payAllTile.data?.data.items[0].caseNo}?isInstallment=true`;
                  } else if (payAllTile.data?.data.items.length > 1) {
                    setIsOpenInstallmentPlanModal(true);
                  }
                }}
              >
                {TranslationService.translate('ApplyForInstallmentsButton')}
              </Button>
            )}
          </div>
        )}
      </PayAllTileDiv>

      {selectedItem && (
        <ProvidersModal
          isOpen={isOpenProvidersModal}
          onToggle={setIsOpenProvidersModal}
          selectedItem={selectedItem}
          onPay={onPay}
        />
      )}

      <CasesModal isOpen={isOpenCasesModal} onToggle={setIsOpenCasesModal} onPayCase={payCase} />

      {isOpenInstallmentPlanModal && payAllTile.data?.data.items && (
        <InstallmentPlanModal
          isOpen={isOpenInstallmentPlanModal}
          onToggle={setIsOpenInstallmentPlanModal}
          cases={payAllTile.data?.data.items}
        />
      )}
    </ModuleContainer>
  );
};

const PayAllTileDiv = styled.div<{
  styles: DictionaryType<DictionaryType<string>>;
}>`
  ${(props) => {
    let styles = '';

    if (props.styles['module:paynow:firstColor'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-color-first: ${props.styles['module:paynow:firstColor'].backgroundColor};`;
    }

    if (props.styles['module:paynow:secondColor'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-color-second: ${props.styles['module:paynow:secondColor'].backgroundColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('textColor')) {
      styles = `${styles} --module-paynow-tile-payButton-color: ${props.styles['module:paynow:payButton'].textColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-payButton-bacground-color: ${props.styles['module:paynow:payButton'].backgroundColor};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('textColorHover')) {
      styles = `${styles} --module-paynow-tile-payButton-color-hover: ${props.styles['module:paynow:payButton'].textColorHover};`;
    }

    if (props.styles['module:paynow:payButton'].hasOwnProperty('backgroundColorHover')) {
      styles = `${styles} --module-paynow-tile-payButton-bacground-color-hover: ${props.styles['module:paynow:payButton'].backgroundColorHover};`;
    }

    if (props.styles['module:paynow:applyButton'].hasOwnProperty('textColor')) {
      styles = `${styles} --module-paynow-tile-applyButton-color: ${props.styles['module:paynow:applyButton'].textColor};`;
    }

    if (props.styles['module:paynow:applyButton'].hasOwnProperty('backgroundColor')) {
      styles = `${styles} --module-paynow-tile-applyButton-bacground-color: ${props.styles['module:paynow:applyButton'].backgroundColor};`;
    }

    if (props.styles['module:paynow:applyButton'].hasOwnProperty('textColorHover')) {
      styles = `${styles} --module-paynow-tile-applyButton-color-hover: ${props.styles['module:paynow:applyButton'].textColorHover};`;
    }

    if (props.styles['module:paynow:applyButton'].hasOwnProperty('backgroundColorHover')) {
      styles = `${styles} --module-paynow-tile-applyButton-bacground-color-hover: ${props.styles['module:paynow:applyButton'].backgroundColorHover};`;
    }

    return css`
      ${styles}
    `;
  }}
`;

export default withAbortRequest(PayAllTile);
